import type { Attribute } from "@commercetools/platform-sdk";
import type { ProductImage } from "../../components/ProductGallery/ProductGallery";
import type { HotspotProps } from "../../components/general/Hotspots/components/Hotspot/Hotspot";
import type { Variant } from "../../templates/product-page/product-page.helper";
import { notNull, notUndefined } from "../type-guards/general";
import { isProductStringAttribute } from "../type-guards/product-attribute";

export interface Image {
	src: string;
	alt?: string | null;
	addPadding?: boolean | null;
	hotspots?: HotspotProps[];
}

const attrListToString = (attrList: string[]): string =>
	attrList
		.map(
			(attr) =>
				attr.toLocaleLowerCase().charAt(0).toLocaleUpperCase() + attr.slice(1),
		)
		.join(", ");

export const getVariantStringAttribute = (
	variant?: Queries.ProductVariantFragment | null,
	name?: string | null,
): string | undefined | null => {
	if (!variant || !name) return undefined;
	const attribute = variant.attributes.find(
		(attribute) => attribute.name === name,
	);
	return isProductStringAttribute(attribute)
		? attribute.stringValue
		: undefined;
};

export const getVariantLabelFromVariant = (
	variant: Variant,
	includeFabric: boolean,
): string => {
	const variantAttributeNames: (keyof Variant)[] = ["color"];
	if (includeFabric) variantAttributeNames.unshift("fabric");

	const labelParts: string[] = [];

	for (const name of variantAttributeNames) {
		const attributeValue = variant[name];
		if (typeof attributeValue == "string" && attributeValue) {
			labelParts.push(attributeValue);
		}
	}

	return attrListToString(labelParts);
};

export const getVariantLabelFromAttributes = (
	attributes: Attribute[] | undefined,
	includeFabric: boolean,
): string => {
	const variantAttributeNames = ["color"];
	if (includeFabric) variantAttributeNames.unshift("fabric");

	const labelParts: string[] = [];

	for (const name of variantAttributeNames) {
		const attribute = attributes?.find((attribute) => attribute.name === name);

		const attributeValue: unknown =
			attribute?.value.label[process.env.GATSBY_LANGUAGE ?? "en-US"];

		if (typeof attributeValue == "string" && attributeValue) {
			labelParts.push(attributeValue);
		}
	}

	return attrListToString(labelParts);
};

export const getVariantColorLabelFromFragment = (
	attributes: Queries.ProductVariantFragment["attributes"],
): string => {
	const variantAttributeNames = ["color"];

	const labelParts: string[] = [];

	for (const name of variantAttributeNames) {
		const attribute = attributes.find(
			(attribute) =>
				attribute.__typename === "CommerceToolsProductAttributeString" &&
				attribute.name === name,
		) as
			| Queries.CommerceToolsAttributes_CommerceToolsProductAttributeString_Fragment
			| undefined;
		if (attribute && attribute.stringValue) {
			labelParts.push(attribute.stringValue);
		}
	}

	return attrListToString(labelParts);
};

export const getVariantFabricLabelFromFragment = (
	attributes: Queries.ProductVariantFragment["attributes"],
): string => {
	const variantAttributeNames = ["fabric"];

	const labelParts: string[] = [];

	for (const name of variantAttributeNames) {
		const attribute = attributes.find(
			(attribute) =>
				attribute.__typename === "CommerceToolsProductAttributeString" &&
				attribute.name === name,
		) as
			| Queries.CommerceToolsAttributes_CommerceToolsProductAttributeString_Fragment
			| undefined;
		if (attribute && attribute.stringValue) {
			labelParts.push(attribute.stringValue);
		}
	}

	return attrListToString(labelParts);
};

export const getVariantImages = (
	variant: Queries.ProductVariantFragment,
): Image[] | undefined | null => {
	const images =
		variant.storyblok_variant_pages?.[0]?.product_variant_images ?? [];

	const sortedImageObject = images.reduce<{
		usedInListing: (typeof images)[0][];
		notUsedInListing: (typeof images)[0][];
	}>(
		(sortedImages, image) => {
			if (image?.use_in_listings)
				sortedImages.usedInListing[sortedImages.usedInListing.length] = image;
			else
				sortedImages.notUsedInListing[sortedImages.notUsedInListing.length] =
					image;

			return sortedImages;
		},
		{ notUsedInListing: [], usedInListing: [] },
	);

	return [
		...sortedImageObject.usedInListing,
		...sortedImageObject.notUsedInListing,
	].reduce<Image[]>((prev, current) => {
		if (
			current?.image?.filename &&
			process.env.GATSBY_STORE &&
			current.stores?.includes(process.env.GATSBY_STORE)
		) {
			return [
				...prev,
				{
					src: current.image.filename,
					alt: current.image.alt,
					addPadding: current.scale_as_product_image,
				},
			];
		}
		return prev;
	}, []);
};

export const getVariantProductImages = (
	variant: Queries.ProductVariantFragment,
): ProductImage[] | undefined | null => {
	const images =
		variant.storyblok_variant_pages?.[0]?.product_variant_images ?? [];

	const reduced = images.reduce<ProductImage[]>((prev, current) => {
		if (
			current?.image?.filename &&
			process.env.GATSBY_STORE &&
			current.stores?.includes(process.env.GATSBY_STORE)
		) {
			const hotspots = mapHotspotsToHotspotsProps(current.hotspots);
			return [
				...prev,
				{
					imgSrc: current.image.filename,
					alt: current.image.alt ?? "",
					isProductImage: current.scale_as_product_image ?? undefined,
					videoSrc: current.video?.filename ?? undefined,
					youtubeEmbedId: current.youtubeEmbedId ?? undefined,
					vimeoResponse: current.vimeo?.vimeo_oembed?.response ?? undefined,
					addPadding: current.scale_as_product_image ?? undefined,
					hotspots: hotspots,
					uid: current._uid ?? undefined,
				},
			];
		}
		return prev;
	}, []);

	return reduced;
};

export const mapHotspotsToHotspotsProps = (
	hotspots?: readonly (Queries.HotspotFragment | null)[] | null,
): HotspotProps[] | undefined => {
	const hotspotProps = hotspots
		? hotspots
				.map<HotspotProps | null>(
					(hotspot) =>
						hotspot && {
							...hotspot,
							content: hotspot.content ?? undefined,
							link: {
								href: hotspot.link?.cached_url ?? "",
								target: hotspot.link?.target ?? "_self",
							},
							image:
								hotspot.image && hotspot.image.filename
									? {
											filename: hotspot.image.filename,
											alt: hotspot.image.alt ?? "",
										}
									: undefined,
							title: hotspot.title ?? undefined,
							xPosition: hotspot.xPosition ?? "0",
							yPosition: hotspot.yPosition ?? "0",
							xPositionMobile: hotspot.xPositionMobile,
							yPositionMobile: hotspot.yPositionMobile,
						},
				)
				.filter(notNull)
		: undefined;
	return hotspotProps;
};

export const getSlugForProductVariant = (
	variant: Queries.Maybe<Queries.ProductVariantFragment> | undefined | null,
): string | undefined | null =>
	variant?.storyblok_variant_pages?.[0]?.full_slug ?? undefined;
interface SortableVariant {
	storyblok_variant_pages?:
		| readonly (
				| {
						sort_order?: string | null;
				  }
				| undefined
				| null
		  )[]
		| null;
}

export const getDefaultVariantForProductPage = <T extends SortableVariant>(
	allVariants: (T | undefined | null)[],
	masterVariant: T | undefined,
) => {
	const someSortOrderSet = allVariants.some(
		(variant) =>
			Number.parseInt(
				variant?.storyblok_variant_pages?.[0]?.sort_order ?? "0",
			) !== 0,
	);

	const sortedVariants = allVariants.sort((variantA, variantB) => {
		const sortOrderA = Number.parseInt(
			variantA?.storyblok_variant_pages?.[0]?.sort_order || "0",
		);
		const sortOrderB = Number.parseInt(
			variantB?.storyblok_variant_pages?.[0]?.sort_order || "0",
		);
		return sortOrderA - sortOrderB;
	});

	const firstDefinedSortedVariant = sortedVariants
		.filter(notNull)
		.find(notUndefined);

	return someSortOrderSet
		? firstDefinedSortedVariant
		: (masterVariant ?? firstDefinedSortedVariant);
};

export const mergeVariants = <T>(
	masterVariant?: T,
	otherVariants?: T[] | null,
): T[] => {
	const filteredOtherVariants = otherVariants
		? otherVariants.filter(notNull).filter(notUndefined)
		: [];

	const allVariants = masterVariant
		? [masterVariant, ...filteredOtherVariants]
		: filteredOtherVariants;
	return allVariants;
};

export const variantName = (color: string, fabric?: string): string => {
	return fabric ? `${color}, ${fabric}` : color;
};
